/**
 * Inicjacja logowania „Zaloguj przez CyberID" (OIDC, zewnętrzny IdP Cyberspace).
 * PKCE robi BACKEND lubbezposrednio: GET /oidc/cyberid/authorize zwraca gotowy authorize_url + state +
 * code_verifier + nonce. Front tylko zapisuje sekrety w sessionStorage i przekierowuje przeglądarkę na CyberID.
 * Powrót ląduje na /logowanie/cyberid/powrot (CyberidCallbackView), które POST-uje code do /oidc/cyberid/callback.
 */
import { cyberidAuthorize } from "@lubbezposrednio-frontend/core";

const SS_KEYS = {
  state: "cyberid_state",
  verifier: "cyberid_verifier",
  nonce: "cyberid_nonce",
  redirect: "cyberid_redirect"
} as const;

/** redirect_uri MUSI być identyczny w #authorize i #callback (CyberID waliduje zgodność). */
export const CYBERID_REDIRECT_URI = `${process.env.REACT_APP_URL}/logowanie/cyberid/powrot`;

/** Rozpoczyna logowanie: pobiera authorize_url z backendu, zapisuje sekrety (state/verifier/nonce), przekierowuje na CyberID. */
export async function startCyberidLogin(redirectPath?: string): Promise<void> {
  const { data } = await cyberidAuthorize(CYBERID_REDIRECT_URI);
  sessionStorage.setItem(SS_KEYS.state, data.state);
  sessionStorage.setItem(SS_KEYS.verifier, data.code_verifier);
  sessionStorage.setItem(SS_KEYS.nonce, data.nonce);
  if (redirectPath) sessionStorage.setItem(SS_KEYS.redirect, redirectPath);
  window.location.href = data.authorize_url;
}

/** Odczyt + walidacja stanu w callbacku (anty-CSRF). Zwraca null gdy state niezgodny lub brak sekretów. */
export function consumeCyberidCallbackState(returnedState: string | null) {
  const state = sessionStorage.getItem(SS_KEYS.state);
  const verifier = sessionStorage.getItem(SS_KEYS.verifier);
  const nonce = sessionStorage.getItem(SS_KEYS.nonce);
  const redirect = sessionStorage.getItem(SS_KEYS.redirect);

  Object.values(SS_KEYS).forEach(k => sessionStorage.removeItem(k));

  if (!returnedState || !state || returnedState !== state || !verifier || !nonce) return null;
  return { verifier, nonce, redirect };
}

// ── Weryfikacja tożsamości wyborcy (per głosowanie) — osobny zestaw kluczy niż logowanie ──

const VERIFY_SS_KEYS = {
  state: "cyberid_v_state",
  verifier: "cyberid_v_verifier",
  nonce: "cyberid_v_nonce",
  voting: "cyberid_v_voting"
} as const;

/** redirect_uri weryfikacji — statyczny (CyberID waliduje dokładną zgodność); voting_id trzymamy w sessionStorage. */
export const CYBERID_VERIFY_REDIRECT_URI = `${process.env.REACT_APP_URL}/weryfikacja/cyberid/powrot`;

/** Rozpoczyna weryfikację tożsamości przez CyberID dla danego głosowania. */
export async function startCyberidVerify(votingId: string): Promise<void> {
  const { data } = await cyberidAuthorize(CYBERID_VERIFY_REDIRECT_URI);
  sessionStorage.setItem(VERIFY_SS_KEYS.state, data.state);
  sessionStorage.setItem(VERIFY_SS_KEYS.verifier, data.code_verifier);
  sessionStorage.setItem(VERIFY_SS_KEYS.nonce, data.nonce);
  sessionStorage.setItem(VERIFY_SS_KEYS.voting, votingId);
  window.location.href = data.authorize_url;
}

/** Odczyt + walidacja stanu weryfikacji w callbacku (anty-CSRF). Zwraca null gdy niezgodny lub brak sekretów. */
export function consumeCyberidVerifyState(returnedState: string | null) {
  const state = sessionStorage.getItem(VERIFY_SS_KEYS.state);
  const verifier = sessionStorage.getItem(VERIFY_SS_KEYS.verifier);
  const nonce = sessionStorage.getItem(VERIFY_SS_KEYS.nonce);
  const votingId = sessionStorage.getItem(VERIFY_SS_KEYS.voting);

  Object.values(VERIFY_SS_KEYS).forEach(k => sessionStorage.removeItem(k));

  if (!returnedState || !state || returnedState !== state || !verifier || !nonce || !votingId) return null;
  return { verifier, nonce, votingId };
}

/** Zapis asercji weryfikacji dla głosowania — czytana przez VotingViewModel przy oddaniu głosu. */
export function storeCyberidAssertion(votingId: string, assertion: string, email: string): void {
  try {
    sessionStorage.setItem(`cyberid_assertion_${votingId}`, JSON.stringify({ assertion, email }));
  } catch {
    /* sessionStorage niedostępny (tryb prywatny) — wyborca zweryfikuje ponownie */
  }
}

/** Odczyt zapisanej asercji weryfikacji dla głosowania (null gdy brak / niepoprawna). */
export function readCyberidAssertion(votingId: string): { assertion: string; email: string } | null {
  try {
    const raw = sessionStorage.getItem(`cyberid_assertion_${votingId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.assertion === "string" && typeof parsed.email === "string") return parsed;
    return null;
  } catch {
    return null;
  }
}
